// ============================================
// moldClaw - Payment Failed Page
// ============================================

/**
 * 🧑‍💻 SWE-2: 결제 실패 페이지
 * 🎨 CX-2: 한국 SaaS 결제 실패 UX 벤치마킹
 *   - 토스: 실패 시 "다시 시도" 버튼이 바로 보임 ✅
 *   - 명확한 실패 사유 표시
 *   - 대체 결제 수단 안내
 */

import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { usePayment } from '../hooks/usePayment';

// 실패 코드별 메시지
const FAILURE_MESSAGES: Record<string, { title: string; description: string; suggestion: string }> = {
  CARD_LIMIT_EXCEEDED: {
    title: '카드 한도 초과',
    description: '카드 결제 한도가 초과되었습니다.',
    suggestion: '카드사에 한도 상향을 요청하시거나 다른 카드를 사용해보세요.',
  },
  CARD_EXPIRED: {
    title: '카드 유효기간 만료',
    description: '등록된 카드의 유효기간이 만료되었습니다.',
    suggestion: '새로운 카드를 등록해주세요.',
  },
  INSUFFICIENT_BALANCE: {
    title: '잔액 부족',
    description: '카드 또는 계좌 잔액이 부족합니다.',
    suggestion: '잔액을 확인하시거나 다른 결제 수단을 사용해보세요.',
  },
  CARD_DECLINED: {
    title: '카드 거절됨',
    description: '카드사에서 결제를 거절했습니다.',
    suggestion: '카드사에 문의하시거나 다른 카드를 사용해보세요.',
  },
  USER_CANCEL: {
    title: '결제 취소됨',
    description: '결제를 취소하셨습니다.',
    suggestion: '결제를 다시 시도하시려면 아래 버튼을 눌러주세요.',
  },
  NETWORK_ERROR: {
    title: '네트워크 오류',
    description: '결제 처리 중 네트워크 문제가 발생했습니다.',
    suggestion: '인터넷 연결을 확인하고 다시 시도해주세요.',
  },
  DEFAULT: {
    title: '결제 실패',
    description: '결제 처리 중 문제가 발생했습니다.',
    suggestion: '잠시 후 다시 시도하시거나 다른 결제 수단을 사용해보세요.',
  },
};

export default function PaymentFailed() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { billingKeys } = usePayment();
  
  // URL 파라미터에서 실패 정보 추출
  const errorCode = searchParams.get('code') || 'DEFAULT';
  const errorMessage = searchParams.get('message');
  const tier = searchParams.get('tier') as 'pro' | 'team' | null;
  
  const failureInfo = FAILURE_MESSAGES[errorCode] || FAILURE_MESSAGES.DEFAULT;
  
  // 다시 시도 핸들러
  const handleRetry = () => {
    // Pricing 페이지로 돌아가서 다시 결제 시도
    if (tier) {
      navigate(`/pricing?tier=${tier}`);
    } else {
      navigate('/pricing');
    }
  };
  
  // 다른 카드 사용
  const handleUseOtherCard = () => {
    navigate('/billing?tab=payment-methods');
  };
  
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Failed Card */}
        <div className="bg-gray-800 rounded-2xl p-8 text-center shadow-xl">
          {/* Failed Icon */}
          <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
            <svg
              className="w-10 h-10 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">
            {failureInfo.title}
          </h1>
          
          <p className="text-gray-400 mb-2">
            {failureInfo.description}
          </p>
          
          {/* Error Message (if provided) */}
          {errorMessage && errorMessage !== failureInfo.description && (
            <p className="text-sm text-red-400 mb-4 bg-red-500/10 rounded-lg p-2">
              상세: {errorMessage}
            </p>
          )}
          
          {/* Suggestion */}
          <div className="bg-gray-700/50 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm text-gray-300">
              💡 {failureInfo.suggestion}
            </p>
          </div>
          
          {/* Actions - 💬 CX-2: 재시도 버튼이 가장 눈에 띄게! */}
          <div className="space-y-3">
            {/* Primary: 다시 시도 */}
            <button
              onClick={handleRetry}
              className="w-full py-3 px-4 bg-space-blue hover:bg-space-blue hover:brightness-110 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              다시 시도
            </button>
            
            {/* Secondary: 다른 카드 사용 */}
            {billingKeys.length > 1 && (
              <button
                onClick={handleUseOtherCard}
                className="w-full py-3 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                다른 카드로 결제
              </button>
            )}
            
            {/* Add new card */}
            <button
              onClick={() => navigate('/billing?tab=payment-methods&add=true')}
              className="w-full py-3 px-4 border border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white rounded-lg font-medium transition-colors"
            >
              + 새 카드 등록
            </button>
            
            {/* Back to Dashboard */}
            <Link
              to="/dashboard"
              className="block text-sm text-gray-500 hover:text-gray-400 transition-colors mt-4"
            >
              대시보드로 돌아가기
            </Link>
          </div>
        </div>
        
        {/* Help Options */}
        <div className="mt-6 bg-gray-800/50 rounded-xl p-4">
          <p className="text-gray-400 text-sm mb-3">계속 문제가 발생하나요?</p>
          <div className="flex flex-col sm:flex-row gap-2 text-sm">
            <a
              href="mailto:support@moldclaw.com"
              className="text-space-blue-light hover:text-space-blue-light transition-colors"
            >
              📧 이메일 문의
            </a>
            <span className="hidden sm:inline text-gray-600">|</span>
            <a
              href="https://help.moldclaw.com/payment"
              target="_blank"
              rel="noopener noreferrer"
              className="text-space-blue-light hover:text-space-blue-light transition-colors"
            >
              📖 결제 FAQ
            </a>
          </div>
        </div>
        
        {/* Error Code (for debugging) */}
        <p className="text-center text-gray-600 text-xs mt-4">
          오류 코드: {errorCode}
        </p>
      </div>
    </div>
  );
}
